/**
 * SSOT לבסיס מחיר חבילת LDS (FlightsHotel) לפני חישוב handleFee נוכחי + עמלת אשראי ב-context.
 * לא משתמשים ב-handleFee / handlingFee / serviceFee כבסיס לסה״כ החבילה.
 */

import { ROOM_KEYS } from "../../Components/PassengersData/PassengersData";

function num(v) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : NaN;
}

function round2(n) {
  return Math.round(n * 100) / 100;
}

function savedOurPrice(order) {
  if (!order || typeof order !== "object") return null;
  return order.ourprice || order.ourPrice || null;
}

/**
 * מחירי בסיס (לפני דמי טיפול מה-handleFeeData הנוכחי) מהזמנה שמורה.
 */
function baseFromSavedOurPrice(sp) {
  if (!sp || typeof sp !== "object") return NaN;
  const pwc = num(sp.priceWithCommission);
  if (pwc > 0) return pwc;

  const pwhf = num(sp.priceWithHandleFee);
  const hf = num(sp.handleFee ?? sp.handlingFee ?? sp.serviceFee);
  if (pwhf > 0 && hf >= 0 && pwhf > hf) {
    return round2(pwhf - hf);
  }
  return NaN;
}

/** בסיס עסקי מ-ourTotalFare של חדר (אותם שדות כמו ourprice) */
function baseFromOurTotalFare(tf) {
  if (!tf || typeof tf !== "object") return NaN;
  const pwc = num(tf.priceWithCommission);
  if (pwc > 0) return pwc;
  return baseFromSavedOurPrice(tf);
}

function savedPassengerSlot(order, index) {
  if (!order || typeof index !== "number" || index < 0) return null;
  const p = order.passengersinfo ?? order.passengersInfo;
  if (!Array.isArray(p) || index >= p.length) return null;
  return p[index];
}

/**
 * כש-ourTotalFare בטבלת החדרים חלקי (למשל priceWithCommission=0 אבל יש סכום "מלא" שגוי),
 * מחזירים בסיס priceWithCommission אמיתי לחישוב תצוגה — אותה משפחת מקורות כמו סה״כ החבילה.
 *
 * @returns {number|null} בסיס לפני שכבת handleFeeData+CC של כרטיס (כמו priceWithCommission)
 */
export function resolveLdsRoomPriceWithCommissionForDisplay({
  ourTotalFare,
  ourSavedLDSOrder,
  selectedResult,
  roomsAndPaxInfo,
  roomsPriceTable,
  roomAndPaxInfoIndex,
  roomSeqNum,
}) {
  const tf = ourTotalFare;
  if (!tf || typeof tf !== "object") return null;

  const direct = baseFromOurTotalFare(tf);
  if (direct > 0) return direct;

  const slot = savedPassengerSlot(ourSavedLDSOrder, roomAndPaxInfoIndex);
  const fromSlot = baseFromOurTotalFare(slot?.room?.ourFareDetails?.ourTotalFare);
  if (fromSlot > 0) return fromSlot;

  if (roomSeqNum != null && roomSeqNum !== "") {
    const passengers =
      ourSavedLDSOrder?.passengersinfo ?? ourSavedLDSOrder?.passengersInfo;
    if (Array.isArray(passengers)) {
      for (let i = 0; i < passengers.length; i++) {
        const r = passengers[i]?.room;
        if (!r) continue;
        const seq = r[ROOM_KEYS.SEQ_NUM] ?? r["@seqNum"];
        if (seq !== roomSeqNum) continue;
        const b = baseFromOurTotalFare(r?.ourFareDetails?.ourTotalFare);
        if (b > 0) return b;
      }
    }
  }

  const n = roomsAndPaxInfo?.length || 1;
  const pkgBase = resolveLdsPackageBasePriceWithCommissionFallback({
    aggregatedPriceWithCommission: 0,
    ourSavedLDSOrder,
    selectedResult,
    roomsAndPaxInfo,
    roomsPriceTable,
  });

  if (pkgBase != null && pkgBase > 0) {
    if (n === 1) return pkgBase;

    const row = roomsPriceTable?.[roomAndPaxInfoIndex];
    if (Array.isArray(row) && row.length > 0) {
      const weights = row.map((cell) =>
        Math.max(
          num(cell?.roomSuggestion?.totalFare) ||
            num(cell?.ourFareDetails?.ourTotalFare?.priceWithCommission) ||
            0,
          0
        )
      );
      const sumW = weights.reduce((a, b) => a + b, 0);
      const idx = row.findIndex(
        (c) => c?.roomSuggestion?.["@seqNum"] === roomSeqNum
      );
      if (idx >= 0 && sumW > 0 && weights[idx] > 0) {
        return round2(pkgBase * (weights[idx] / sumW));
      }
    }
    return round2(pkgBase / n);
  }

  const sr = num(selectedResult?.ourPrice?.ourTotalFare?.priceWithCommission);
  if (sr > 0) return n > 1 ? round2(sr / n) : sr;

  return null;
}

/**
 * סכום priceWithCommission לפי בחירת החדרים בטבלת המחירים (כשהמידע ב-room state חסר/שבור).
 */
export function sumLdsSelectedRoomsPriceWithCommissionFromTable(
  roomsAndPaxInfo,
  roomsPriceTable
) {
  if (!Array.isArray(roomsAndPaxInfo) || !Array.isArray(roomsPriceTable)) {
    return NaN;
  }
  let sum = 0;
  let countedRooms = 0;
  for (let i = 0; i < roomsAndPaxInfo.length; i++) {
    const selSeq = roomsAndPaxInfo[i]?.room?.[ROOM_KEYS.SEQ_NUM];
    if (selSeq == null || selSeq === "") continue;
    const row = roomsPriceTable[i];
    if (!Array.isArray(row)) continue;
    const found = row.find(
      (entry) => entry?.roomSuggestion?.["@seqNum"] === selSeq
    );
    const pwc = num(found?.ourFareDetails?.ourTotalFare?.priceWithCommission);
    if (pwc > 0) {
      sum += pwc;
      countedRooms += 1;
    }
  }
  return countedRooms > 0 ? round2(sum) : NaN;
}

/**
 * @param {object} params
 * @param {number} params.aggregatedPriceWithCommission — סכום אחרי לולאת החדרים ב-context
 * @param {object|null} params.ourSavedLDSOrder
 * @param {object|null} params.selectedResult
 * @param {Array} params.roomsAndPaxInfo
 * @param {Array} params.roomsPriceTable
 * @returns {number|null} בסיס priceWithCommission ב-₪ (או מטבע התצוגה הקיים אצלכם), או null
 */
export function resolveLdsPackageBasePriceWithCommissionFallback({
  aggregatedPriceWithCommission,
  ourSavedLDSOrder,
  selectedResult,
  roomsAndPaxInfo,
  roomsPriceTable,
}) {
  const agg = num(aggregatedPriceWithCommission);
  if (agg > 0) return null;

  const tryVals = [];

  const sp = savedOurPrice(ourSavedLDSOrder);
  const fromSaved = baseFromSavedOurPrice(sp);
  if (fromSaved > 0) tryVals.push({ v: fromSaved, src: "saved.ourprice" });

  const srPwc = num(selectedResult?.ourPrice?.ourTotalFare?.priceWithCommission);
  if (srPwc > 0) tryVals.push({ v: srPwc, src: "selectedResult.ourPrice.ourTotalFare" });

  const fromTable = sumLdsSelectedRoomsPriceWithCommissionFromTable(
    roomsAndPaxInfo,
    roomsPriceTable
  );
  if (fromTable > 0) tryVals.push({ v: fromTable, src: "roomsPriceTable" });

  const picked = tryVals[0];
  if (picked && picked.v > 0) {
    if (
      process.env.NODE_ENV === "development" &&
      typeof console !== "undefined" &&
      console.log
    ) {
      console.log("[resolveLdsPackageBasePriceWithCommissionFallback]", {
        source: picked.src,
        value: picked.v,
        candidates: tryVals,
      });
    }
    return picked.v;
  }

  return null;
}
